<template>
  <div class="bg-background-light dark:bg-background-dark min-h-screen flex flex-col font-display antialiased text-[#181611]">

    <!-- ══════════════════════════════════════════════════════════
         TERMS GATE — shown once per user before activation
    ══════════════════════════════════════════════════════════ -->
    <div v-if="state === 'terms'" class="min-h-screen flex flex-col">

      <!-- Header bar -->
      <div class="bg-[#1a2235] px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div class="flex items-center gap-3">
          <div class="h-9 w-9 flex items-center justify-center">
            <img src="/abc_logo.png" alt="ABC Tax Advisory" class="h-8 w-auto object-contain" />
          </div>
          <div>
            <p class="text-white font-bold text-sm leading-tight">ABC Tax Advisory</p>
            <p class="text-slate-400 text-xs">Client Portal</p>
          </div>
        </div>
        <span class="text-xs text-slate-400 hidden sm:block">Terms Acceptance Required — v1.0</span>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto bg-slate-50 px-4 py-8">
        <div class="max-w-3xl mx-auto">

          <!-- Title -->
          <div class="text-center mb-8">
            <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold mb-4">
              <span class="material-symbols-outlined !text-[14px]">gavel</span>
              Action Required Before Proceeding
            </div>
            <h1 class="text-2xl font-bold text-slate-900 mb-2">Terms &amp; Conditions</h1>
            <p class="text-slate-500 text-sm max-w-xl mx-auto">
              Welcome, <strong>{{ invite && invite.Title }}</strong>. Please read the following terms carefully before accessing your portal.
            </p>
          </div>

          <!-- Combined Terms Document -->
          <div class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6">

            <!-- Document header -->
            <div class="flex items-center gap-4 px-6 py-5 border-b border-slate-100 bg-slate-50">
              <div class="h-10 w-10 rounded-xl bg-[#1a2235] flex items-center justify-center flex-shrink-0">
                <span class="material-symbols-outlined text-white !text-[20px]">description</span>
              </div>
              <div>
                <p class="text-sm font-bold text-slate-900">ABC Tax Advisory — Portal Terms, Conditions &amp; Client Authority</p>
                <p class="text-xs text-slate-400">ABN: 17 666 126 870 · Level 15, 60 Station Street, Parramatta NSW 2150 · v1.0</p>
              </div>
            </div>

            <!-- Scrollable terms body -->
            <div class="overflow-y-auto px-6 py-5 text-xs text-slate-600 leading-relaxed space-y-5" style="max-height: 420px;">

              <!-- Part A heading -->
              <div class="flex items-center gap-2 pb-2 border-b border-slate-100">
                <div class="h-5 w-5 rounded bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <span class="material-symbols-outlined text-blue-600 !text-[13px]">article</span>
                </div>
                <p class="text-xs font-bold text-slate-700 uppercase tracking-wider">Part A — Cloud Portal Terms &amp; Conditions and Liability Waiver</p>
              </div>

              <div v-for="clause in termsDoc" :key="clause.heading" class="space-y-1">
                <p class="font-semibold text-slate-800">{{ clause.heading }}</p>
                <p>{{ clause.body }}</p>
              </div>

              <!-- Part B heading -->
              <div class="flex items-center gap-2 pb-2 border-b border-slate-100 pt-4">
                <div class="h-5 w-5 rounded bg-green-50 flex items-center justify-center flex-shrink-0">
                  <span class="material-symbols-outlined text-green-600 !text-[13px]">assignment_ind</span>
                </div>
                <p class="text-xs font-bold text-slate-700 uppercase tracking-wider">Part B — Client Authority &amp; Acknowledgment</p>
              </div>

              <p class="text-slate-400 text-xs">This Client Authority forms part of the onboarding process for clients of ABC Tax Advisory Pty Ltd.</p>

              <div v-for="clause in authorityDoc" :key="clause.heading" class="space-y-1">
                <p class="font-semibold text-slate-800">{{ clause.heading }}</p>
                <p>{{ clause.body }}</p>
              </div>
            </div>
          </div>

          <!-- Checkbox + CTA -->
          <div class="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-5">

            <!-- Checkbox -->
            <label class="flex items-start gap-3 cursor-pointer group">
              <div class="relative flex-shrink-0 mt-0.5">
                <input type="checkbox" v-model="termsAccepted" class="sr-only" />
                <div
                  class="h-5 w-5 rounded border-2 flex items-center justify-center transition-all duration-200"
                  :class="termsAccepted
                    ? 'bg-[#1a2235] border-[#1a2235]'
                    : 'border-slate-300 bg-white group-hover:border-slate-400'">
                  <span v-if="termsAccepted" class="material-symbols-outlined text-white !text-[14px]">check</span>
                </div>
              </div>
              <span class="text-sm text-slate-700 leading-relaxed">
                I confirm that I have read, understood and agree to be legally bound by the
                <strong class="text-slate-900">Cloud Portal Terms &amp; Conditions and Liability Waiver</strong>
                and the
                <strong class="text-slate-900">Client Authority &amp; Acknowledgment</strong>
                above, and that I am authorised to upload and access documents for myself or the relevant client entity.
              </span>
            </label>

            <!-- Error -->
            <div v-if="termsError" class="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
              <span class="material-symbols-outlined !text-[18px]">error</span>
              {{ termsError }}
            </div>

            <!-- CTA button -->
            <button
              @click="handleAgree"
              :disabled="!termsAccepted || recordingAcceptance"
              class="w-full flex items-center justify-center gap-2 px-10 py-4 font-bold rounded-xl transition-all duration-300 text-sm"
              :class="termsAccepted && !recordingAcceptance
                ? 'bg-[#1a2235] text-white hover:bg-[#252f47] shadow-lg cursor-pointer'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed'">
              <svg v-if="recordingAcceptance" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span class="material-symbols-outlined !text-[18px]" v-else>check_circle</span>
              {{ recordingAcceptance ? 'Recording your acceptance...' : 'I Agree &amp; Continue' }}
            </button>

            <p class="text-center text-xs text-slate-400">
              Your acceptance is recorded with a timestamp and IP address for legal compliance purposes.
            </p>
          </div>

        </div>
      </div>

      <!-- Footer -->
      <footer class="bg-white border-t border-slate-200 px-6 py-4 flex-shrink-0">
        <div class="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <p class="text-xs text-slate-400">© {{ currentYear }} ABC Tax Advisory Pty Ltd — ABN 17 666 126 870</p>
          <div class="flex items-center gap-3 text-xs text-slate-400">
            <a href="https://abctaxadvisory.com.au" target="_blank" class="hover:text-[#00c2e0] transition-colors">Website</a>
            <span>•</span>
            <a href="tel:0286063665" class="hover:text-[#00c2e0] transition-colors">02 8606 3665</a>
            <span>•</span>
            <a href="https://abctaxadvisory.com.au/contact" target="_blank" class="hover:text-[#00c2e0] transition-colors">Contact Us</a>
          </div>
        </div>
      </footer>
    </div>

    <!-- ══════════════════════════════════════════════════════════
         ORIGINAL INVITE FLOW (all other states)
    ══════════════════════════════════════════════════════════ -->
    <div v-else class="flex-grow flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div class="w-full max-w-[480px] bg-white dark:bg-[#2A261C] rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#e5e7eb] dark:border-[#3E382A] overflow-hidden relative z-10">

        <div class="h-2 w-full bg-brand-dark"></div>

        <div class="p-8 sm:p-10">

          <!-- Logo -->
          <div class="flex items-center gap-3 mb-8">
            <img src="/abc_logo.png" alt="ABC Tax Advisory" class="h-10 w-auto object-contain" />
          </div>

          <!-- loading -->
          <div v-if="state === 'loading'" class="flex flex-col items-center gap-4 py-10">
            <svg class="animate-spin h-10 w-10 text-brand-dark" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="text-gray-500 text-sm">Validating your invitation...</p>
          </div>



          <!-- invalid -->
          <div v-else-if="state === 'invalid'" class="py-2">
            <div class="flex flex-col items-center text-center mb-6">
              <div class="size-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
                <span class="material-symbols-outlined text-red-500 !text-[32px]">link_off</span>
              </div>
              <h2 class="text-xl font-semibold mb-2">Invalid Invitation</h2>
              <p class="text-gray-500 text-sm">{{ errorMessage }}</p>
            </div>
            <div class="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
              <div class="flex gap-3">
                <span class="material-symbols-outlined !text-[20px] text-blue-600 flex-shrink-0">info</span>
                <div class="text-sm text-blue-600">
                  <p class="font-semibold mb-1">What to do next</p>
                  <p class="text-xs">Contact your tax advisor to request a new invitation link. Invitation links expire 7 days after being sent.</p>
                </div>
              </div>
            </div>
            <div class="mt-2 pt-6 border-t border-gray-100 flex items-center justify-center gap-2 text-gray-400">
              <span class="material-symbols-outlined !text-[16px]">support_agent</span>
              <a href="https://abctaxadvisory.com.au/contact" target="_blank"
                class="text-xs font-medium uppercase tracking-wider hover:text-brand-dark transition-colors">
                Contact Support
              </a>
            </div>
          </div>



          <!-- success -->
          <div v-else-if="state === 'success'" class="flex flex-col items-center text-center py-8">
            <div class="size-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
              <span class="material-symbols-outlined text-green-500 !text-[32px]">check_circle</span>
            </div>
            <h2 class="text-xl font-semibold mb-2">You're All Set!</h2>
            <p class="text-gray-500 text-sm mb-2">Your terms acceptance has been recorded.</p>
            <p class="text-gray-500 text-sm mb-6">Redirecting you to the login page...</p>
            <svg class="animate-spin h-6 w-6 text-brand-dark" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>

        </div>

        <!-- Card footer -->
        <footer class="px-8 pb-6 flex items-center justify-center gap-2 text-xs text-gray-400">
          <span>© {{ currentYear }} ABC Tax Advisory</span>
          <span>•</span>
          <a href="https://abctaxadvisory.com.au" target="_blank" class="hover:text-brand-dark transition-colors">Website</a>
          <span>•</span>
          <a href="https://abctaxadvisory.com.au/contact" target="_blank" class="hover:text-brand-dark transition-colors">Contact Support</a>
        </footer>
      </div>
    </div>

  </div>
</template>

<script>
const TERMS_VERSION = 'v1.0';

async function callClientData(action, extra = {}) {
  const res = await fetch('/.netlify/functions/client-data', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, clientName: '', ...extra })
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Server error');
  return json.data;
}

export default {
  name: 'InvitePage',

  data() {
    return {
      state: 'loading',   // loading | terms | valid | success | invalid
      invite: null,
      errorMessage: '',
      recordingAcceptance: false,
      termsAccepted: false,
      termsError: '',
      currentYear: new Date().getFullYear(),
      token: '',

      termsDoc: [
        { heading: '1. Click-Wrap Acceptance', body: "By clicking 'I Agree', accessing or using this cloud portal, you confirm that you have read, understood and agree to be legally bound by these Terms & Conditions and Liability Waiver. You also confirm that you are authorised to upload, access and manage documents for yourself or the relevant client entity." },
        { heading: '2. Purpose of the Portal', body: 'The portal provides a facility for the electronic upload, storage and exchange of documents in connection with tax agent and related professional services provided by ABC Tax Advisory Pty Ltd.' },
        { heading: '3. Confidentiality and Professional Obligations', body: 'We are a tax agent registered with the Tax Practitioners Board and are bound by the Code of Professional Conduct under the Tax Agent Services Act 2009 (Cth), including obligations relating to confidentiality of client information, subject to legal exceptions.' },
        { heading: '4. Privacy and Information Security', body: 'We take reasonable steps to protect personal information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles, including Australian Privacy Principle 11. You acknowledge that no electronic system can be guaranteed to be completely secure.' },
        { heading: '5. Ownership of Client Documents', body: 'All documents and information uploaded to or stored in the portal remain the property of the client.' },
        { heading: '6. Irrevocable Licence to Use Documents', body: 'You grant ABC Tax Advisory Pty Ltd an irrevocable, perpetual, royalty-free licence to store, copy, use, process and rely upon documents uploaded to the portal for the purposes of providing services, complying with legal and professional obligations, record-keeping, audit, risk management and quality assurance.' },
        { heading: '7. Custodial Holding and No Verification', body: 'ABC Tax Advisory Pty Ltd holds client documents in a custodial capacity only and does not verify, audit or warrant the accuracy, completeness or legality of uploaded documents.' },
        { heading: '8. Client Responsibility and Risk Allocation', body: 'Documents are uploaded entirely at your own risk. You remain responsible for maintaining original records and backups. To the maximum extent permitted by law, ABC Tax Advisory Pty Ltd accepts no responsibility or liability for loss, destruction, corruption, unauthorised access or inability to retrieve uploaded documents.' },
        { heading: '9. Third-Party Cloud Service Providers', body: 'The portal may be hosted or supported by third-party cloud service providers. You expressly consent to your documents being stored and processed using such providers, including where data may be stored or accessed outside Australia.' },
        { heading: '10. Availability of the Portal', body: "Access to the portal is provided on an 'as available' basis. We may suspend, restrict or terminate access at any time for maintenance, security, compliance or operational reasons." },
        { heading: '11. Limitation of Liability', body: 'To the maximum extent permitted by law, the total aggregate liability of ABC Tax Advisory Pty Ltd arising out of or in connection with the portal or any uploaded documents is limited to the total professional fees actually paid by you to ABC Tax Advisory Pty Ltd in the twelve (12) months immediately preceding the event giving rise to the claim.' },
        { heading: '12. Exclusion of Indirect Loss', body: 'We are not liable for any indirect or consequential loss, including loss of data, profit, revenue, opportunity, goodwill or business interruption.' },
        { heading: '13. Governing Law', body: 'These Terms & Conditions and Liability Waiver are governed by the laws of New South Wales, Australia.' },
        { heading: '14. Contact', body: 'For any questions regarding these terms, please contact ABC Tax Advisory Pty Ltd on 02 8606 3665.' }
      ],

      authorityDoc: [
        { heading: '1. Authority', body: 'You authorise ABC Tax Advisory Pty Ltd to provide tax agent and related professional services to you, and to receive, store, use and rely upon information and documents provided by you for those purposes.' },
        { heading: '2. Authority to Use Cloud Systems', body: 'You authorise ABC Tax Advisory Pty Ltd to store your documents and information electronically using cloud-based systems and third-party service providers for the purposes of service delivery, compliance and record-keeping.' },
        { heading: '3. Client Acknowledgment – Security and Risk', body: 'You acknowledge that electronic storage and transmission of information involves inherent risks and that no system can be guaranteed to be completely secure. You accept those risks.' },
        { heading: '4. Responsibility for Documents', body: 'You confirm that all documents provided or uploaded by you are complete, accurate and authorised. You remain responsible for maintaining your own copies and backups of all documents.' },
        { heading: '5. No Guarantee of Retention', body: 'You acknowledge that the cloud portal is provided as a convenience only and that ABC Tax Advisory Pty Ltd does not guarantee the ongoing availability, retention or recoverability of uploaded documents.' },
        { heading: '6. Consent to Electronic Communication', body: 'You consent to communication with ABC Tax Advisory Pty Ltd by electronic means, including via the client portal, email and other digital platforms.' }
      ]
    };
  },

  async mounted() {
    this.token = this.$route.query.token || '';
    if (!this.token) {
      this.errorMessage = 'No invitation token found in this link. Please use the link sent to your email.';
      this.state = 'invalid';
      return;
    }
    await this.validateToken();
  },

  methods: {

    async validateToken() {
      this.state = 'loading';
      try {
        // 1. Validate the invite token server-side (no MSAL needed)
        const result = await callClientData('validateInvitation', { inviteToken: this.token });
        if (!result.valid) {
          this.errorMessage = result.reason;
          this.state = 'invalid';
          return;
        }
        this.invite = result.invite;

        // 2. Check acceptance server-side (source of truth — not localStorage)
        const acceptCheck = await callClientData('checkAcceptance', {
          clientEmail: result.invite.Email,
          documentVersion: 'v1.0'
        });
        if (acceptCheck && acceptCheck.accepted) {
          // Already accepted in SharePoint — go straight to login
          this.markTermsAccepted(result.invite.Email); // keep localStorage in sync
          this.$router.push('/login');
          return;
        }

        // 3. Show terms gate
        this.state = 'terms';
      } catch (err) {
        console.error('Token validation error:', err);
        this.errorMessage = 'Unable to validate your invitation. Please try again or contact support.';
        this.state = 'invalid';
      }
    },

    hasAcceptedTerms(email) {
      try {
        const stored = localStorage.getItem('terms_accepted_' + email);
        if (!stored) return false;
        return JSON.parse(stored).version === TERMS_VERSION;
      } catch { return false; }
    },

    markTermsAccepted(email) {
      try {
        localStorage.setItem('terms_accepted_' + email, JSON.stringify({ version: TERMS_VERSION, ts: new Date().toISOString() }));
      } catch (e) { /* localStorage not available */ }
    },

    async handleAgree() {
      this.recordingAcceptance = true;
      this.termsError = '';
      try {
        const clientName = this.invite?.Title || 'Unknown';
        const clientEmail = this.invite?.Email || '';

        // 1. Record acceptance (PDF + SharePoint list)
        const res = await fetch('/.netlify/functions/record-acceptance', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clientName, clientEmail })
        });
        if (!res.ok) throw new Error('Server error recording acceptance');

        // 2. Mark invitation as accepted in SharePoint
        await callClientData('acceptInvitation', { inviteGraphId: this.invite.graphId });

        // 3. Mark locally so terms never show again for this version
        this.markTermsAccepted(clientEmail);

        // 4. Show success then redirect to login
        this.state = 'success';
        setTimeout(() => { this.$router.push('/login'); }, 2500);

      } catch (err) {
        console.error('Terms acceptance error:', err);
        this.termsError = 'Something went wrong recording your acceptance. Please try again.';
      } finally {
        this.recordingAcceptance = false;
      }
    },

    formatExpiry(dateString) {
      if (!dateString) return 'soon';
      return new Date(dateString).toLocaleDateString('en-AU', { month: 'long', day: 'numeric', year: 'numeric' });
    }
  }
};
</script>

<style>
body { font-family: 'Public Sans', sans-serif; }
.material-symbols-outlined { font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24; }
</style>